import React from 'react';
import moment from 'moment';
import './OrderSummary.css';

const dateTime = value => value && moment(value).isValid() ? moment(value).format('MM/DD/YYYY hh:mm:ss a') : '';

function Detail({ label, children }) {
  return <div className="order-summary-field"><dt>{label}</dt><dd>{children === undefined || children === null || children === '' ? '-' : children}</dd></div>;
}

export default function OrderSummary({
  order, clientId, shipments, tracking, weight, returns, serials, notes, attachments,
  onSaveContact, onDeleteShipment, onDeleteAttachment, messages,
  returnPath = '/return', customerView = false
}) {
  const shipment = shipments[0];
  const hasNotes = Boolean(order.or_internal_notes) || notes.length > 0;
  const indicators = [
    { field: 'or_expedited', label: 'Expedited', variant: 'expedited' },
    { field: 'or_misshipped', label: 'Mis-shipped', variant: 'misshipped' },
    { field: 'or_palletized', label: 'Palletized', variant: 'palletized' }
  ].filter(indicator => Number(order[indicator.field]) === 1);
  const legacyIndicators = ['EXPEDITED', 'MIS-SHIPPED', 'PALLETIZED'];
  const otherAddressLine = order.or_other && !legacyIndicators.includes(String(order.or_other).trim().toUpperCase())
    ? order.or_other
    : null;
  return (
    <section className="order-summary" aria-label="Order summary">
      <div className="order-summary-grid">
        <div className="order-summary-primary">
          <section className="order-summary-section">
            <h4>Ship to</h4>
            <address>
              <strong>{order.or_ship_fname} {order.or_ship_lname}</strong>
              {[order.or_ship_company, order.or_ship_attention, order.or_ship_address, order.or_ship_address1,
                [order.or_city, order.or_state, order.or_zip, order.or_country].filter(Boolean).join(' '),
                order.or_ship_email, otherAddressLine].filter(Boolean).map((line, index) => <div key={index}>{line}</div>)}
            </address>
            {onSaveContact && <button type="button" className="btn btn-info btn-sm" onClick={onSaveContact}>Save as New Contact</button>}
          </section>
          <section className="order-summary-section">
            <h4>Order details</h4>
            <dl>
              <Detail label="Order ID">{order.or_actual_order_id}</Detail>
              <Detail label="Order date">{dateTime(order.or_date)}</Detail>
              <Detail label="Ship by">{dateTime(order.or_ship_by)}</Detail>
              <Detail label="Status"><span className="order-summary-status">{order.or_status}</span></Detail>
              {weight.map(row => <Detail key={row.total_order_weight} label="Weight">{row.total_order_weight} lbs.</Detail>)}
            </dl>
            {indicators.length > 0 && <div className="order-summary-indicators" aria-label="Order indicators">
              {indicators.map(indicator => <span
                className={'order-summary-indicator order-summary-indicator-' + indicator.variant}
                key={indicator.field}
              >{indicator.label}</span>)}
            </div>}
            {returns.length > 0 && <div className="order-summary-extra">
              <h5>Returns</h5>
              {returns.map(row => <div key={row.rt_id}>
                <a href={process.env.REACT_APP_ROOT_URL + returnPath + '/' + clientId + '/' + row.rt_id} target="_new">{row.rt_rma_int}</a> ({row.rt_status})
              </div>)}
            </div>}
          </section>
        </div>
        <section className="order-summary-section">
          <h4>Requested shipping</h4>
          {order.or_urgent === 1 && Number(clientId) === 6 && <p className="order-summary-urgent">Amazon Prime - Check Shipping Account!</p>}
          {order.or_urgent === 1 && Number(clientId) === 2 && <p className="order-summary-urgent">Urgent Order - Please Expedite Shipping!</p>}
          <dl>
            <Detail label="Order type">{order.or_type}</Detail>
            {order.or_freight_bol === 'Yes' ? <Detail label="Account">Client Provided BOL</Detail> :
              order.or_type === 'Freight' && <React.Fragment>
                <Detail label="Account">BW Freight Account</Detail>
                <Detail label="Express delivery">{dateTime(order.or_freight_express_date)}</Detail>
                <Detail label="NMFC / description">{order.or_freight_nmfc}</Detail>
                <Detail label="Freight class">{order.or_freight_class}</Detail>
                <Detail label="Contact name">{order.or_wc_name}</Detail>
                <Detail label="Contact phone">{order.or_wc_phone}</Detail>
                <Detail label="Pick up time">{order.or_wc_time}</Detail>
              </React.Fragment>}
            <Detail label="Method">{order.or_ship_method}</Detail>
            {order.or_freight_bol !== 'Yes' && <React.Fragment>
              <Detail label="Carrier">{order.or_carrier}</Detail>
              {order.or_type !== 'Freight' && <Detail label="Service">{order.or_carrier_service}</Detail>}
            </React.Fragment>}
          </dl>
          {order.or_small_acct && <div className="order-summary-extra">
            <h5>Third party account</h5>
            <dl>
              <Detail label="Account info">{order.or_small_acct}</Detail>
              <Detail label="Billing ZIP">{order.or_small_zip}</Detail>
              <Detail label="Billing country">{order.or_small_country}</Detail>
            </dl>
          </div>}
          {order.or_type === 'Will Call' && <div className="order-summary-extra">
            <h5>Will call details</h5>
            <dl>
              <Detail label="Contact name">{order.or_wc_name}</Detail>
              <Detail label="Contact phone">{order.or_wc_phone}</Detail>
              <Detail label="Pick up time">{order.or_wc_time}</Detail>
            </dl>
          </div>}
          {attachments.length > 0 && <div className="order-summary-extra">
            <h5>Attachments</h5>
            <ul className="order-summary-attachments">{attachments.map((file, index) => <li key={index}>
              <a href={'https://bw-admin-files.s3.amazonaws.com/'.concat(file)} target="_new">{file.substr(file.lastIndexOf('/') + 1)}</a>
              {onDeleteAttachment && <button type="button" className="btn btn-info btn-sm" aria-label={'Delete attachment ' + file.substr(file.lastIndexOf('/') + 1)} onClick={() => onDeleteAttachment(file)}>x</button>}
            </li>)}</ul>
          </div>}
        </section>
        <section className="order-summary-section">
          <h4>Shipment</h4>
          {shipment ? <React.Fragment>
            <dl>
              <Detail label="Ship date">{dateTime(shipment.sh_date)}</Detail>
              <Detail label="Method">{shipment.sh_method}</Detail>
              {customerView
                ? <Detail label="Shipment Cost">${typeof shipment.sh_cost_adj === 'undefined' ? 0 : shipment.sh_cost_adj}</Detail>
                : <React.Fragment>
                  <Detail label="Cost">${typeof shipment.sh_cost === 'undefined' ? 0 : shipment.sh_cost}</Detail>
                  <Detail label="Fuel">${typeof shipment.sh_cost_adj === 'undefined' ? 0 : shipment.sh_cost_adj}</Detail>
                </React.Fragment>}
              <Detail label="Pallets">{shipment.sh_pallets}</Detail>
              <Detail label="Notes">{shipment.sh_notes}</Detail>
              <Detail label="BOL #">{shipment.sh_bol}</Detail>
            </dl>
            {tracking.length > 0 && <h5>Tracking #</h5>}
            {tracking.map(row => <div className="order-summary-tracking" key={row.sh_id}>
              <strong>{row.sh_tracking}</strong>
              <div style={{ color: row.sh_scan_date == null ? '#b42318' : '#18703b' }}>
                {row.sh_scan_date == null ? 'not verified' : 'verified: ' + dateTime(row.sh_scan_date)}
              </div>
            </div>)}
            {onDeleteShipment && <button type="button" className="btn btn-info btn-sm" onClick={() => onDeleteShipment(shipment)}>Delete Shipment</button>}
          </React.Fragment> : <p className="order-summary-muted">No shipment yet</p>}
          {serials.length > 0 && Number(clientId) === 22 && <div className="order-summary-extra">
            <h5>Serials</h5>
            {serials.map(row => <div key={row.ot_sku}>{row.ot_sku} ({row.pr_sku})</div>)}
          </div>}
        </section>
      <div className="order-summary-context">
        {hasNotes && <section className="order-summary-notes" aria-label="Order notes">
          <h4>Order notes</h4>
          {order.or_internal_notes && <div className="order-summary-note order-summary-internal">
            <h5>Internal notes</h5>
            <div>{order.or_internal_notes}</div>
          </div>}
          {notes.map(note => <article className="order-summary-note" key={note.on_id}>
            <div className="order-summary-note-meta">{moment(note.on_date).format('MM/DD/YYYY hh:mm:ss A')} {note.user}</div>
            <div>{note.on_note}</div>
            <div className="order-summary-note-meta">Hours: {note.or_hours}</div>
          </article>)}
        </section>}
      </div>
      </div>
      {messages.some(Boolean) && <div className="order-summary-feedback" role="status">
        {messages.filter(Boolean).map((message, index) => <div key={index}>{message}</div>)}
      </div>}
    </section>
  );
}
